/**
 * Import Monitor — live ERP shortage report.
 *
 * The ERP is the source of truth. Overdue schedule lines the ERP sent in its
 * latest complete push that are short in the ERP (or in Helix only), each
 * marked with how Helix lines up. Lines out of line with the ERP are
 * highlighted for repair on the Helix side, unless someone accepts the
 * difference. Refreshes with every push; the banner says which push the page
 * reflects and warns when pushes have stopped arriving.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  erpShortageAPI,
  type ErpShortageFilters,
  type ErpShortageOrdering,
  type ErpShortageOverdue,
  type ErpShortageResponse,
  type ErpShortageRow,
  type ErpShortageView,
} from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { useMenuSettings } from '../../contexts/MenuSettingsContext';
import { useFormatters } from '../../contexts/PreferencesContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { AlertTriangle, CheckCircle2, Copy, Download, Loader2, RefreshCw, Search } from 'lucide-react';
import { toast } from 'sonner';
import AcceptShortageDifferenceDialog from './AcceptShortageDifferenceDialog';
import ErpMismatchRepairModal from './ErpMismatchRepairModal';
import ErpShortageTable, { ALIGNMENT_META, ERP_ORDER_TYPE_LABELS } from './ErpShortageTable';
import FixNextDayDuplicatesDialog from './FixNextDayDuplicatesDialog';
import { localTodayIso } from './GrnRepairCards';

interface ImportErpShortageTabProps {
  customerId: number;
}

const PAGE_SIZE = 25;
const NO_PUSH_MESSAGE = 'No complete ERP push has been received yet.';
const selectClass = 'text-sm border rounded-md py-2 px-2 bg-background';

async function errorDetail(err: unknown, fallback: string): Promise<string> {
  const data = (err as { response?: { data?: unknown } })?.response?.data;
  try {
    const body = data instanceof Blob ? JSON.parse(await data.text()) : data;
    const detail = (body as { detail?: unknown } | undefined)?.detail;
    return typeof detail === 'string' ? detail : fallback;
  } catch {
    return fallback;
  }
}

export default function ImportErpShortageTab({ customerId }: ImportErpShortageTabProps) {
  const { formatNumber } = useFormatters();
  const { user } = useAuth();
  const { isDashboardTabEnabled, isFeatureEnabled } = useMenuSettings();
  // Same rule as GRN Repair (server enforces it too): CR-6274 module switch,
  // Scheduled install, then superuser or staff.
  const canRepair = isFeatureEnabled('grn_repair')
    && isDashboardTabEnabled('scheduled')
    && (!!user?.is_superuser || !!user?.is_staff);
  const [repairTarget, setRepairTarget] = useState<{ poLineId: number; label: string } | null>(null);
  const [acceptTarget, setAcceptTarget] = useState<ErpShortageRow | null>(null);
  const [accepting, setAccepting] = useState(false);
  const [revokingId, setRevokingId] = useState<number | null>(null);
  const [data, setData] = useState<ErpShortageResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [view, setView] = useState<ErpShortageView | ''>('');
  const [supplierId, setSupplierId] = useState<number | ''>('');
  const [buyer, setBuyer] = useState('');
  const [warehouse, setWarehouse] = useState('');
  const [orderType, setOrderType] = useState('');
  const [overdue, setOverdue] = useState<ErpShortageOverdue | ''>('');
  const [ordering, setOrdering] = useState<ErpShortageOrdering>('overdue');
  const [page, setPage] = useState(1);
  // Ticked next-day duplicate pairs (earlier slot ids); kept across pages, cleared when the filters change.
  const [selectedPairs, setSelectedPairs] = useState<Set<number>>(new Set());
  const [fixOpen, setFixOpen] = useState(false);

  const filters: ErpShortageFilters = {
    view: view || undefined,
    search: search || undefined,
    supplierId: supplierId || undefined,
    buyer: buyer || undefined,
    warehouse: warehouse || undefined,
    orderType: orderType || undefined,
    overdue: overdue || undefined,
    ordering,
    today: localTodayIso(),
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await erpShortageAPI.list(customerId, {
        view: view || undefined,
        search: search || undefined,
        supplierId: supplierId || undefined,
        buyer: buyer || undefined,
        warehouse: warehouse || undefined,
        orderType: orderType || undefined,
        overdue: overdue || undefined,
        ordering,
        today: localTodayIso(),
      }, page, PAGE_SIZE));
    } catch (err: unknown) {
      console.error('ERP shortage report load failed:', err);
      setError('Failed to load the ERP shortage report');
      toast.error('Failed to load the ERP shortage report');
    } finally {
      setLoading(false);
    }
  }, [customerId, view, search, supplierId, buyer, warehouse, orderType, overdue, ordering, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setSelectedPairs(new Set()); }, [customerId, view, search, supplierId, buyer, warehouse, orderType, overdue]);

  const selectPairs = (ids: number[], checked: boolean) => setSelectedPairs((prev) => {
    const next = new Set(prev);
    ids.forEach((id) => (checked ? next.add(id) : next.delete(id)));
    return next;
  });

  const handleExport = async () => {
    setExporting(true);
    try {
      const blob = await erpShortageAPI.exportXlsx(customerId, filters);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `erp_shortage_report_${localTodayIso()}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Excel exported successfully');
    } catch (err: unknown) {
      console.error('ERP shortage report export failed:', err);
      toast.error(await errorDetail(err, 'Failed to export Excel'));
    } finally {
      setExporting(false);
    }
  };

  const handleAccept = async (reason: string) => {
    if (!acceptTarget) return;
    setAccepting(true);
    try {
      await erpShortageAPI.acceptDifference(acceptTarget.schedule_id, reason);
      toast.success(`Difference accepted on ${acceptTarget.po_number}/${acceptTarget.line_number}`);
      setAcceptTarget(null);
      load();
    } catch (err: unknown) {
      console.error('Accept difference failed:', err);
      toast.error(await errorDetail(err, 'Failed to accept the difference'));
    } finally {
      setAccepting(false);
    }
  };

  const handleRevoke = async (row: ErpShortageRow) => {
    setRevokingId(row.schedule_id);
    try {
      await erpShortageAPI.revokeDifference(row.schedule_id);
      toast.success(`Acceptance revoked on ${row.po_number}/${row.line_number} — it needs repair again`);
      load();
    } catch (err: unknown) {
      console.error('Revoke difference failed:', err);
      toast.error(await errorDetail(err, 'Failed to revoke the acceptance'));
    } finally {
      setRevokingId(null);
    }
  };

  const chooseView = (next: ErpShortageView | '') => { setView(next); setPage(1); };
  const totalPages = data ? Math.max(1, Math.ceil(data.count / PAGE_SIZE)) : 1;
  const summary = data?.summary;
  const push = data?.push;
  const facets = data?.facets;
  const narrowed = !!(view || search || supplierId || buyer || warehouse || orderType || overdue);
  const canFixDuplicates = !!data?.can_fix_duplicates;
  const duplicatePairs = summary?.next_day_duplicate_pairs ?? 0;

  if (data && !data.enabled) {
    return (
      <div className="py-12 text-center text-sm text-muted-foreground">
        ERP balance qty isn&apos;t captured for this customer&apos;s import, so there is no ERP shortage report.
      </div>
    );
  }

  const pushTime = push?.completed_at ? new Date(push.completed_at) : null;
  const card = (key: ErpShortageView | '', title: string, value: string, tone: string, note?: string) => (
    <button type="button" onClick={() => chooseView(view === key ? '' : key)} aria-pressed={view === key}
      className={`text-left rounded-lg border bg-card p-4 transition hover:shadow-sm ${view === key ? 'ring-2 ring-primary' : ''}`}>
      <p className="text-xs uppercase text-muted-foreground">{title}</p>
      <p className={`text-2xl font-semibold tabular-nums ${tone}`}>{value}</p>
      {note && <p className="text-xs text-muted-foreground">{note}</p>}
    </button>
  );

  return (
    <div className="space-y-4">
      {/* Push freshness — which ERP push this page reflects */}
      {data && push && !push.available && (
        <div className="flex gap-2 items-start rounded-md border border-slate-300 bg-slate-50 p-3 text-sm text-slate-700">
          <Loader2 className="h-4 w-4 mt-0.5 shrink-0" />
          <span>{NO_PUSH_MESSAGE} The report fills in once the ERP has sent a full push that arrived whole.</span>
        </div>
      )}
      {push?.available && pushTime && (
        <div className={`flex gap-2 items-start rounded-md border p-3 text-sm ${push.stale ? 'border-amber-300 bg-amber-50 text-amber-900' : 'border-green-200 bg-green-50 text-green-900'}`}>
          {push.stale ? <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" /> : <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />}
          <span>
            ERP is the source of truth — as of the ERP push completed{' '}
            <strong>{pushTime.toLocaleString(undefined, { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</strong>
            {' '}({push.slots?.toLocaleString()} schedule lines sent).
            {push.stale && ' No complete push for over three hours — the ERP may have changed since.'}
          </span>
        </div>
      )}

      {/* Summary — click a card to show just those lines */}
      {summary && (
        <div className={`grid grid-cols-2 gap-3 ${canFixDuplicates ? 'md:grid-cols-3 xl:grid-cols-6' : 'md:grid-cols-5'}`}>
          {card('erp_shortage', 'ERP shortage lines', summary.erp_shortage_lines.toLocaleString(), 'text-blue-700',
            `${formatNumber(summary.erp_short_qty)} qty short in the ERP`)}
          {card('needs_repair', 'Needs repair in Helix', summary.needs_repair.toLocaleString(), 'text-red-700',
            'Helix out of line with the ERP')}
          {card('aligned', 'Aligned', summary.by_alignment.aligned.toLocaleString(), 'text-green-700',
            'Helix matches the ERP')}
          {card('accepted', 'Accepted differences', summary.by_alignment.accepted.toLocaleString(), 'text-slate-700',
            'Accepted as correct')}
          {card('short_in_helix_only', 'Short in Helix only', summary.by_alignment.short_in_helix_only.toLocaleString(), 'text-orange-600',
            'ERP has these cleared')}
          {canFixDuplicates && card('next_day_duplicates', 'Next-day ERP duplicates', duplicatePairs.toLocaleString(), 'text-violet-700',
            duplicatePairs === 1 ? 'Pair to fix — list it to select' : 'Pairs to fix — list them to select')}
        </div>
      )}

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <input
            className="pl-8 pr-3 py-2 text-sm border rounded-md w-64"
            placeholder="PO, PO/line, part or supplier…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { setSearch(searchInput.trim()); setPage(1); } }}
          />
        </div>
        <select className={selectClass} aria-label="Show" value={view}
          onChange={(e) => chooseView(e.target.value as ErpShortageView | '')}>
          <option value="">All lines{summary ? ` · ${summary.lines}` : ''}</option>
          <option value="erp_shortage">ERP shortage report{summary ? ` · ${summary.erp_shortage_lines}` : ''}</option>
          <option value="needs_repair">Needs repair in Helix{summary ? ` · ${summary.needs_repair}` : ''}</option>
          {canFixDuplicates && (
            <option value="next_day_duplicates">Next-day ERP duplicates{summary ? ` · ${duplicatePairs} pairs` : ''}</option>
          )}
          {(['aligned', 'accepted', 'helix_received_more', 'helix_missing_receipts', 'cancelled_in_helix', 'short_in_helix_only'] as const).map((a) => (
            <option key={a} value={a}>{ALIGNMENT_META[a].label}{summary ? ` · ${summary.by_alignment[a]}` : ''}</option>
          ))}
        </select>
        <select className={selectClass} aria-label="Supplier" value={supplierId}
          onChange={(e) => { setSupplierId(e.target.value ? Number(e.target.value) : ''); setPage(1); }}>
          <option value="">All suppliers</option>
          {(facets?.suppliers ?? []).map((s) => (
            <option key={s.id} value={s.id}>{s.name}{s.code ? ` (${s.code})` : ''} · {s.count}</option>
          ))}
        </select>
        <select className={selectClass} aria-label="Buyer" value={buyer}
          onChange={(e) => { setBuyer(e.target.value); setPage(1); }}>
          <option value="">All buyers</option>
          {(facets?.buyers ?? []).map((b) => <option key={b.code} value={b.code}>{b.code} · {b.count}</option>)}
        </select>
        <select className={selectClass} aria-label="Warehouse" value={warehouse}
          onChange={(e) => { setWarehouse(e.target.value); setPage(1); }}>
          <option value="">All warehouses</option>
          {(facets?.warehouses ?? []).map((w) => <option key={w.code} value={w.code}>{w.code} · {w.count}</option>)}
        </select>
        <select className={selectClass} aria-label="ERP order type" value={orderType}
          onChange={(e) => { setOrderType(e.target.value); setPage(1); }}>
          <option value="">All order types</option>
          {(facets?.order_types ?? []).map((o) => (
            <option key={o.code} value={o.code}>
              {o.code}{ERP_ORDER_TYPE_LABELS[o.code] ? ` — ${ERP_ORDER_TYPE_LABELS[o.code]}` : ''} · {o.count}
            </option>
          ))}
        </select>
        <select className={selectClass} aria-label="Days overdue" value={overdue}
          onChange={(e) => { setOverdue(e.target.value as ErpShortageOverdue | ''); setPage(1); }}>
          <option value="">Any days overdue</option>
          <option value="1_7">1–7 days</option>
          <option value="8_14">8–14 days</option>
          <option value="15_30">15–30 days</option>
          <option value="30_plus">Over 30 days</option>
        </select>
        <select className={selectClass} aria-label="Sort" value={ordering}
          onChange={(e) => { setOrdering(e.target.value as ErpShortageOrdering); setPage(1); }}>
          <option value="overdue">Most overdue first</option>
          <option value="recent">Most recent first</option>
          <option value="difference">Largest Helix/ERP difference</option>
          <option value="erp_short">Largest ERP balance</option>
          <option value="supplier">Supplier A–Z</option>
        </select>
        <Button variant="outline" size="sm" onClick={() => load()} disabled={loading} aria-label="Refresh">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
        </Button>
        <Button variant="outline" size="sm" onClick={handleExport} disabled={exporting || !data || data.count === 0} className="gap-2">
          {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
          Export
        </Button>
        {canFixDuplicates && (
          <Button size="sm" className="gap-2" onClick={() => setFixOpen(true)}
            disabled={!data?.push.available || (selectedPairs.size === 0 && duplicatePairs === 0)}
            title="The ERP repeated a slot the next day and booked the receipt against the repeat: move the receipt there, cancel the earlier slot and accept its ERP balance">
            <Copy className="h-4 w-4" />
            {selectedPairs.size > 0
              ? `Fix ${selectedPairs.size} selected next-day duplicate${selectedPairs.size === 1 ? '' : 's'}`
              : `Fix all next-day duplicates · ${duplicatePairs}`}
          </Button>
        )}
        {selectedPairs.size > 0 && (
          <Button variant="ghost" size="sm" onClick={() => setSelectedPairs(new Set())}>Clear selection</Button>
        )}
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading && !data ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
            </div>
          ) : error ? (
            <div className="py-12 text-center text-sm text-red-600">{error}</div>
          ) : !data || !data.push.available ? (
            <div className="py-12 text-center text-sm text-muted-foreground">{NO_PUSH_MESSAGE}</div>
          ) : data.results.length === 0 ? (
            <div className="py-12 text-center text-sm text-muted-foreground">
              {narrowed ? 'No lines match these filters.' : 'No overdue schedule lines are short in the ERP or in Helix — all clear.'}
            </div>
          ) : (
            <ErpShortageTable
              rows={data.results}
              canRepair={canRepair}
              canAccept={!!data.can_accept_differences}
              onRepair={(r) => setRepairTarget({ poLineId: r.po_line_id, label: `${r.po_number}/${r.line_number}` })}
              onAccept={setAcceptTarget}
              onRevoke={handleRevoke}
              revokingId={revokingId}
              selectable={canFixDuplicates}
              selected={selectedPairs}
              onSelect={selectPairs}
            />
          )}
        </CardContent>
      </Card>

      <ErpMismatchRepairModal
        poLineId={repairTarget?.poLineId ?? null}
        label={repairTarget?.label ?? ''}
        onClose={() => setRepairTarget(null)}
        onChanged={load}
      />
      <FixNextDayDuplicatesDialog
        open={fixOpen}
        customerId={customerId}
        filters={filters}
        selectedIds={selectedPairs}
        today={localTodayIso()}
        onClose={() => setFixOpen(false)}
        onFixed={() => { setFixOpen(false); setSelectedPairs(new Set()); load(); }}
      />
      <AcceptShortageDifferenceDialog
        row={acceptTarget}
        saving={accepting}
        onClose={() => setAcceptTarget(null)}
        onAccept={handleAccept}
      />

      {/* Pagination */}
      {data && data.count > PAGE_SIZE && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">
            Page {data.page} of {totalPages} · {data.count.toLocaleString()} lines
          </span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1 || loading} onClick={() => setPage(p => p - 1)}>Previous</Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages || loading} onClick={() => setPage(p => p + 1)}>Next</Button>
          </div>
        </div>
      )}
    </div>
  );
}
